import { Text } from '@mantine/core'
import { NOT_MEASURED } from '../../utils/format'
import { episodeCellText } from '../../types/retirementEpisodes'
import type { RetirementEpisodeStateInfo } from '../../types/retirementEpisodes'

/**
 * One report's retirement episode, in a table cell: "Kept until <date>", "Open
 * episode", or the dash.
 *
 * `state` is the report's entry from useEpisodeStates, or null — for a report
 * with no episode (the by-report response omits it) and equally while that query
 * is loading or has failed. The caller decides how to caption a failed column;
 * a cell never paints a guess.
 *
 * Neutral text throughout, never green: an open episode is a report on the
 * consent clock, and a keep is a person's decision to hold it — neither is a
 * healthy state to celebrate. A terminal episode with nothing in force renders
 * the dash with its state as the title, so hovering says what the dash hides.
 */
export function EpisodeStateCell({ state }: { state: RetirementEpisodeStateInfo | null }) {
  const text = episodeCellText(state)

  if (text == null) {
    return (
      <Text size="sm" c="dimmed" title={state ? `Latest episode: ${state.state}` : undefined}>
        {NOT_MEASURED}
      </Text>
    )
  }

  // The latest episode's keep reason, offered only when that episode IS the keep —
  // never beside a reversal's quiet period, which no person gave a reason for.
  const reason = state?.state === 'Kept' && !state.liveKeepIsReversalQuietPeriod ? state.keepReason : null

  return (
    <Text size="sm" title={reason ? `Kept because: ${reason}` : undefined}>
      {text}
    </Text>
  )
}
